import { CLASSES_AND_SPECS, SPECS_TO_HERO_TREES } from "./classes-and-specs";
import { WOW_TREE_DATA_PATH } from "./constants";
import { sigfigFormatter } from "./tierHelper";
import manifest from "./season-manifest.json";

// From the backend season manifest (Common.SeasonManifest); resync the local
// copy with `yarn sync-manifest`.
export const PATCH = manifest.patch;
export const SEASON = manifest.season;
export const EXPANSION = manifest.expansion;

export const getIsHealer = (spec) => {
  const healingSpecs = ["restoration", "mistweaver", "holy", "discipline", "preservation"];
  return healingSpecs.includes(spec.toLowerCase());
};

export const getIsTank = (spec) => {
  const tankSpecs = ["protection", "blood", "vengeance", "brewmaster", "guardian"];
  return tankSpecs.includes(spec.toLowerCase());
};

export const getFormattedRole = (role) => {
  switch (role.toLowerCase()) {
    case "dps":
      return "DPS";
    case "heal":
      return "Healer";
    default:
      return "Tank";
  }
};

export const getIcyVeinsRole = (spec) => {
  if (getIsHealer(spec)) {
    return "healing";
  }
  if (getIsTank(spec)) {
    return "tank";
  }
  return "dps";
};

export const getParsedSelectedTalents = (selectedTalentsData) =>
  selectedTalentsData.reduce(
    (acc, item) => {
      const parsedTalent = item.split("#");

      acc.selectedTalents.push(parsedTalent[0]);
      acc.selectedTalentNodeIds.push(parsedTalent[1]);
      acc.selectedTalentsValues.push(parsedTalent[2]);

      return acc;
    },
    { selectedTalents: [], selectedTalentNodeIds: [], selectedTalentsValues: [] },
  );

export const getItemQualityColor = (quality) => {
  switch (quality) {
    case 0: // poor
      return "#9d9d9d";
    case 1: // common
      return "#ffffff";
    case 2: // uncommon
      return "#1eff00";
    case 3: // rare
      return "#0070dd";
    case 4: // epic
      return "#a335ee";
    case 5: // legendary
      return "#ff8000";
    case 6: // artifact
      return "#e6cc80";
    default:
      // heirloom
      return "#00ccff";
  }
};

export const getAbbreviatedClass = (className) => {
  switch (className.toLowerCase()) {
    case "death knight":
      return "DK";
    case "demon hunter":
      return "DH";
    default:
      return className;
  }
};

export const getAbbreviatedSpec = (specName) => {
  switch (specName.toLowerCase()) {
    case "restoration":
      return "Resto";
    case "beast mastery":
      return "BM";
    case "protection":
      return "Prot";
    case "retribution":
      return "Ret";
    case "discipline":
      return "Disc";
    case "demonology":
      return "Demo";
    case "destruction":
      return "Destro";
    default:
      return specName;
  }
};

export const getHeroTalentIconURL = (heroName) => {
  if (!heroName || heroName.trim() === "") return "";

  const modifiedName = heroName
    .split(" ")
    .map((el) => el[0].toUpperCase() + el.slice(1))
    .join("_");

  return `https://static.bigbrain.gg/wow/hero-icons/${modifiedName}.png`;
};

export const getHeroTalentQueryParamFormat = (string) => {
  if (!string || string.trim() === "") return "";

  return string.replace(/'/g, "").replace(/\s/g, "_").toLowerCase();
};

export const getHeroNameFromSelectedTree = (selectedTree, displayClassName, displaySpecName) => {
  return SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][selectedTree === "left" ? 0 : 1] ?? "";
};

// Picks which hero tree to default to when the URL doesn't specify one: higher tier first,
// then the page's primary score (dps/hps/rating), then parses. Each side is
// { tierLetter, score, parses }; missing values lose to present ones.
export const pickBetterHeroSide = (left, right) => {
  const tierRank = { "S+": 6, S: 5, A: 4, B: 3, C: 2, D: 1 };
  const compare = (l, r) => (l > r ? "left" : r > l ? "right" : null);

  return (
    compare(tierRank[left?.tierLetter] ?? 0, tierRank[right?.tierLetter] ?? 0) ??
    compare(left?.score ?? 0, right?.score ?? 0) ??
    compare(left?.parses ?? 0, right?.parses ?? 0) ??
    "left"
  );
};

export const getDataHeroName = (heroName) => {
  if (!heroName || heroName.trim() === "") return "";

  return heroName
    .split(" ")
    .map((el) => el[0].toUpperCase() + el.slice(1))
    .join("")
    .replace(/'/g, "''");
};

export const getFormattedContentType = (type) => {
  if (type?.includes("mythic") ?? false) {
    return "Mythic+";
  } else if (type === "raid") {
    return "The Venomous Abyss";
  } else if (type.toLowerCase() === "pvp") {
    return "PvP";
  }
  return type;
};

export const getSpecTreeAccessor = (classType) => {
  // In our specTree we use death_knight whereas the class name is DeathKnight
  const regexp = /[A-Z]/g;
  // find all capital letters
  const array = [...classType.matchAll(regexp)];
  if (array.length <= 1) {
    return classType.toLowerCase();
  } else {
    let splitText = classType;
    for (let i = 1; i < array.length; i++) {
      splitText = splitText.replace(array[i], `_${array[i]}`.toLowerCase());
    }
    return splitText.toLowerCase();
  }
};

export const getSpecTree = (specTrees, classType, spec) => {
  const classAccessor = getSpecTreeAccessor(classType);
  const specAccessor = getSpecTreeAccessor(spec);
  return specTrees[classAccessor][specAccessor];
};

export const getClassTree = (specTrees, classType) => {
  const classAccessor = getSpecTreeAccessor(classType);
  const specAccessor = "common";
  return specTrees[classAccessor][specAccessor];
};

export const getFormattedEncounterType = (encounterType) => {
  if (encounterType === "mythic") {
    encounterType = "mythic+";
  } else if (encounterType === "pvp") {
    encounterType = "PvP";
  }

  return encounterType.slice(0, 1).toUpperCase() + encounterType.slice(1);
};

export const getSeoContentEncounter = (encounterType) => {
  let result = "The Venomous Abyss";
  if (encounterType === "mythic") {
    result = "Mythic +";
  } else if (encounterType === "pvp") {
    result = `Season ${SEASON} PvP`;
  }
  return result;
};

export const getSeoContentEncounterTwo = (encounterType) => {
  let result = "raid";
  if (encounterType === "mythic") {
    result = "m+";
  } else if (encounterType === "pvp") {
    result = "pvp";
  }
  return result;
};

export const getIcyVeinsLinkName = (originalName) => {
  return originalName.split("_").join("-");
};

export const convertDisplayNameToDataName = (displayName) => {
  if (!displayName || displayName.trim() === "") return "";

  switch (displayName.toLowerCase()) {
    case "deathknight":
      return "death_knight";
    case "demonhunter":
      return "demon_hunter";
    case "beastmastery":
      return "beast_mastery";
    default:
      return displayName.toLowerCase().split(" ").join("_");
  }
};

export const isClassHunter = (className) => {
  if (className == "hunter") {
    return "-pet";
  } else {
    return "";
  }
};

export const getDisplayName = (originalName) => {
  if (!originalName || originalName.trim() === "") return "";

  return originalName
    .split("_")
    .map((el) => el[0].toUpperCase() + el.slice(1))
    .join(" ");
};

export const getDataName = (originalName) => {
  if (!originalName || originalName.trim() === "") return "";

  return originalName
    .split("_")
    .map((el) => el[0].toUpperCase() + el.slice(1))
    .join("");
};

export const getFormattedNames = (classType, specType) => {
  const displaySpecName = getDisplayName(specType);
  const displayClassName = getDisplayName(classType);
  const dataSpecName = getDataName(specType);
  const dataClassName = getDataName(classType);

  return { displayClassName, displaySpecName, dataClassName, dataSpecName };
};

export const parseBuildRows = (tree, selectedTalents, rows, sortAsc) => {
  if (!tree) {
    return [];
  }

  let nodes = Object.entries(tree).filter(([key, node]) => selectedTalents.includes(node.id.toString()));

  if (rows !== "all") {
    nodes = nodes.filter(([key, node]) => rows.includes(node.row));
  }

  // Sort by highest row and lowest column. E.g. [10, 5], [10, 8], [10, 17], [9, 5], [9, 9], etc.
  const descCompareFunc = (a, b) => {
    // We multiply by 100 to check rows first. It must be 100 since there are 17 columns, so we can't do 10
    return b[1].row * 100 - a[1].row * 100 + a[1].column - b[1].column;
  };

  // Sort by lowest row and highest column. E.g. [9, 5], [9,9], [10,5], [10,8], [10, 17], etc.
  const ascCompareFunc = (a, b) => {
    // We multiply by 100 to check rows first. It must be 100 since there are 17 columns, so we can't do 10
    return a[1].row * 100 - b[1].row * 100 + b[1].column - a[1].column;
  };

  return sortAsc ? nodes.sort(ascCompareFunc) : nodes.sort(descCompareFunc);
};

export const parseBuildRow = (tree, selectedTalents, row) => {
  return Object.entries(tree).filter(([key, node]) => {
    return node.row == row && selectedTalents.includes(node.id.toString());
  });
};

export const getCapstoneTalents = (tree, selectedTalents) => {
  return parseBuildRow(tree, selectedTalents, 10);
};

export const getChoiceNodes = (tree, selectedTalents) => {
  return Object.entries(tree).filter(([key, node]) => {
    return node.type == 3 && selectedTalents.includes(node.id.toString());
  });
};

export const getClassColor = (displayClassName) => {
  const classData = CLASSES_AND_SPECS.filter((obj) => obj.class === displayClassName);
  return classData[0].color;
};

export const getMainStatData = (encounterType, specType, maxKey, hps, dps) => {
  let mainStat = `+${maxKey}`;
  let statLabel = "Max Key";
  let statTooltip = "The highest key that this particular talent build was used in and completed on time.";

  if (encounterType === "raid") {
    if (getIsHealer(specType)) {
      mainStat = sigfigFormatter(hps);
      statLabel = "HPS";
      statTooltip =
        "HPS is the aggregated Healing Per Second score that each Healer spec does when using this talent build in the top rankings of Raid and Mythic+ Dungeons.";
    } else if (getIsTank(specType)) {
      mainStat = sigfigFormatter(dps);
      statLabel = "Tank DPS";
      statTooltip =
        "For Tanks we opted to show their aggregated DPS score when using this talent build as a temporary placeholder metric while we search for a more valuable metric that tank players care about.";
    } else {
      mainStat = sigfigFormatter(dps);
      statLabel = "DPS";
      statTooltip =
        "DPS is the aggregated Damage Per Second score that each DPS spec does when using this talent build in the top rankings of Raid and Mythic+ Dungeons.";
    }
  }

  return { mainStat, statLabel, statTooltip };
};

export const getSpritesheetUrl = (classType, specType, treeType) => {
  return `${WOW_TREE_DATA_PATH}/assets/${classType}_${specType}.webp`;
};

export const normalizeName = (str) => {
  if (!str) {
    return "";
  }

  return str
    .split(/ |_/)
    .map((x) => x[0].toUpperCase() + x.slice(1))
    .join("");
};

const difficultyPath = {
  all: "all",
  normal: "3",
  heroic: "4",
  mythic: "5",
};

export const getDifficultyPath = (diff, encounterType) => {
  // Matches the declared `difficulty` defaults in query_parameters/valid_parameters.js.
  // Callers that read difficulty straight off the query string rely on this fallback.
  if (!diff) {
    diff = "all";
  }

  // if (!diff) return difficultyPath["mythic"];

  return difficultyPath[diff];
};

export const getSpecOrClassIcon = (isClass, dataClassName, dataSpecName) =>
  isClass
    ? `https://static.bigbrain.gg/wow/class-icons/ClassIcon_${dataClassName}.jpg`
    : `https://static.bigbrain.gg/wow/spec-icons/${dataClassName}-${dataSpecName}.jpg`;

export const getPrimaryStatLabel = (primaryStats) =>
  Object.keys(primaryStats).find((x) => !!primaryStats[x] && parseFloat(primaryStats[x]) !== -1 && x !== "stamina");

export const getFactionFromNum = (num) => (num === 1 ? "alliance" : "horde");

export const getPrimaryStat = (classType, specType) => {
  const primaryStats = {
    DeathKnight: {
      Blood: "Strength",
      Frost: "Strength",
      Unholy: "Strength",
    },
    DemonHunter: {
      Havoc: "Agility",
      Vengeance: "Agility",
      Devourer: "Intellect",
    },
    Druid: {
      Balance: "Intellect",
      Feral: "Agility",
      Guardian: "Agility",
      Restoration: "Intellect",
    },
    Evoker: {
      Augmentation: "Intellect",
      Devastation: "Intellect",
      Preservation: "Intellect",
    },
    Hunter: {
      BeastMastery: "Agility",
      Marksmanship: "Agility",
      Survival: "Agility",
    },
    Mage: {
      Arcane: "Intellect",
      Fire: "Intellect",
      Frost: "Intellect",
    },
    Monk: {
      Brewmaster: "Agility",
      Mistweaver: "Intellect",
      Windwalker: "Agility",
    },
    Paladin: {
      Holy: "Intellect",
      Protection: "Strength",
      Retribution: "Strength",
    },
    Priest: {
      Discipline: "Intellect",
      Holy: "Intellect",
      Shadow: "Intellect",
    },
    Rogue: {
      Assassination: "Agility",
      Outlaw: "Agility",
      Subtlety: "Agility",
    },
    Shaman: {
      Elemental: "Intellect",
      Enhancement: "Agility",
      Restoration: "Intellect",
    },
    Warlock: {
      Affliction: "Intellect",
      Demonology: "Intellect",
      Destruction: "Intellect",
    },
    Warrior: {
      Arms: "Strength",
      Fury: "Strength",
      Protection: "Strength",
    },
  };

  return primaryStats[classType][specType];
};
